import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import {
  CheckCircleOutline as AllGoodIcon,
  Payments as PayIcon,
  AccountBalanceWallet as WalletIcon,
  Assignment as WorkIcon,
  Groups as PeopleIcon,
  Description as DocsIcon
} from '@mui/icons-material';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip, CartesianGrid, Cell } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import ApiService from '../../services/api.service';
import WalletService from '../../services/wallet.service';
import { customerCardService } from '../../services/accounting/customerCard.service';
import { portalFeaturesForSession } from '../../services/portal/portalFeatures.service';
import { portalDashboardService } from '../../services/portal/portalDashboard.service';
import { formatDate } from '../../utils/dateFormat';
import { formatCompact } from '../../utils/dashboard/metrics';
import HeadlineCard from '../../components/dashboard/v2/HeadlineCard';
import DashCard from '../../components/dashboard/v2/DashCard';
import AttentionList from '../../components/dashboard/v2/AttentionList';
import { IconTile, gridSx } from '../dashboard/OperationsTab';

const getField = (obj, ...keys) => {
  for (const key of keys) {
    if (obj?.[key] !== undefined && obj?.[key] !== null) return obj[key];
  }
  return null;
};

const formatCurrency = (amount) => new Intl.NumberFormat('en-AE', {
  style: 'currency',
  currency: 'AED'
}).format(parseFloat(amount) || 0);

const totalOf = (res) => {
  const p = res?.data?.data || {};
  return p.total_elements ?? p.totalElements ?? 0;
};

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const listOf = (res) => res?.data?.data || [];
const isoToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const DAY = 24 * 60 * 60 * 1000;
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// One independent block of the page: loads on its own, so a slow or failed call only affects its own card.
const useSection = (load, enabled, key) => {
  const loadRef = useRef(load);
  loadRef.current = load;
  const [state, setState] = useState({ loading: enabled, error: false, data: null });
  useEffect(() => {
    if (!enabled) {
      setState({ loading: false, error: false, data: null });
      return undefined;
    }
    let active = true;
    setState({ loading: true, error: false, data: null });
    loadRef.current()
      .then((data) => { if (active) setState({ loading: false, error: false, data }); })
      .catch(() => { if (active) setState({ loading: false, error: true, data: null }); });
    return () => { active = false; };
  }, [enabled, key]);
  return state;
};

// loading → skeleton, failed → "—", else the value
const show = (s, node, width = 80) => (s.loading ? <Skeleton width={width} /> : s.error ? '—' : node);

const Line = ({ children, hero }) => (
  <Typography component="div" sx={{ fontSize: '0.75rem', color: hero ? 'inherit' : 'text.secondary', opacity: hero ? 0.85 : 1, lineHeight: 1.7 }}>
    {children}
  </Typography>
);

const CardAction = ({ label, onClick, hero }) => (
  <Button
    size="small" variant={hero ? 'outlined' : 'contained'} disableElevation
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    sx={{
      textTransform: 'none', fontSize: '0.75rem', fontWeight: 700, borderRadius: '9px', minWidth: 0, px: 1.5,
      ...(hero ? { color: '#fff', borderColor: 'rgba(255,255,255,.5)', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,.12)' } } : {}),
    }}
  >
    {label}
  </Button>
);

/**
 * Portal dashboard (thaklis-doc 14-portal/PORTAL-DASHBOARD.md): what needs the client's action first,
 * then money, work, people & documents, and recent activity. Every block follows the portal feature
 * switches and loads on its own; Viewers get no action buttons.
 */
const PortalDashboard = ({ portalUser }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const features = portalFeaturesForSession(portalUser);
  const canAct = portalUser?.role !== 'ORG_VIEWER';
  const orgKey = portalUser?.organizationId;

  const quotations = useSection(async () => listOf(await ApiService.getPortalQuotations()), true, orgKey);
  const workOrders = useSection(async () => listOf(await ApiService.getPortalWorkOrders()), true, orgKey);
  const invoices = useSection(async () => listOf(await ApiService.getPortalInvoices()), true, orgKey);
  const outstanding = useSection(async () => {
    const res = await ApiService.getPortalOutstandingInvoicesForOrg();
    const list = res?.data?.data || res?.data || [];
    return Array.isArray(list) ? list : [];
  }, true, orgKey);
  const wallet = useSection(() => WalletService.getPortalWallet(), features.PAYMENTS, orgKey);
  const topups = useSection(
    async () => (await WalletService.getTopupRequests({ organizationId: orgKey || 'portal' }))?.data || [],
    features.PAYMENTS, orgKey
  );
  const tasks = useSection(async () => {
    const [pending, inProgress] = await Promise.all([
      ApiService.getPortalTasks({ page: 0, size: 1, status: 'PENDING' }),
      ApiService.getPortalTasks({ page: 0, size: 1, status: 'IN_PROGRESS' }),
    ]);
    return totalOf(pending) + totalOf(inProgress);
  }, features.TASKS, orgKey);
  const employees = useSection(async () => {
    const payload = (await ApiService.getPortalEmployees(0, 1))?.data?.data || {};
    return Array.isArray(payload) ? payload.length : (payload.totalElements ?? payload.total_elements ?? 0);
  }, features.EMPLOYEES, orgKey);
  const people = useSection(() => portalDashboardService.people(), features.EMPLOYEES, orgKey);
  const expiry = useSection(async () => (await ApiService.getPortalDocumentExpirySummary())?.data?.data || null, features.DOC_EXPIRY, orgKey);
  const companyExpiry = useSection(async () => (await ApiService.getPortalDocumentExpirySummary({ kind: 'COMPANY' }))?.data?.data || null,
    features.DOC_EXPIRY && features.EMPLOYEES, orgKey);
  const cards = useSection(() => customerCardService.getCards(), features.SAVED_CARDS, orgKey);
  const months = useSection(() => portalDashboardService.invoicedByMonth(), true, orgKey);

  const quoteList = quotations.data || [];
  const woList = workOrders.data || [];
  const invoiceList = invoices.data || [];
  const outstandingList = outstanding.data || [];
  const topupList = topups.data || [];
  const walletData = wallet.data;
  const expiryData = expiry.data;

  const today = isoToday();
  const dueOf = (inv) => getField(inv, 'due_date', 'dueDate');
  const amountDueOf = (inv) => Number(getField(inv, 'amount_due', 'amountDue', 'grand_total', 'grandTotal') || 0);
  const overdue = outstandingList.filter((inv) => dueOf(inv) && String(dueOf(inv)).slice(0, 10) < today);
  const overdueTotal = overdue.reduce((s, inv) => s + amountDueOf(inv), 0);
  const outstandingTotal = outstandingList.reduce((s, inv) => s + amountDueOf(inv), 0);
  const toPayList = outstandingList.filter((inv) => !overdue.includes(inv));
  const toPayTotal = outstandingTotal - overdueTotal;

  const awaitingQuotes = quoteList.filter((q) => q.status === 'SENT');
  const pendingReview = woList.filter((w) => w.status === 'PENDING_REVIEW');
  const onHold = woList.filter((w) => w.status === 'ON_HOLD');
  const activeWos = woList.filter((w) => !['COMPLETED', 'CANCELLED', 'INVOICED', 'PENDING_REVIEW'].includes(w.status));
  const completedWos = woList.filter((w) => ['COMPLETED', 'INVOICED'].includes(w.status));
  const pendingTopups = topupList.filter((t) => t.status === 'PENDING');
  const rejectedTopups = topupList
    .filter((t) => t.status === 'REJECTED' && t.reviewedAt && Date.now() - new Date(t.reviewedAt).getTime() <= 30 * DAY)
    .sort((a, b) => new Date(b.reviewedAt) - new Date(a.reviewedAt));
  const topupAmount = (list) => list.reduce((s, t) => s + Number(t.requestedAmount || t.amount || 0), 0);

  const walletBalance = Number(walletData?.approvedBalance || 0);
  const waitingApproval = Number(walletData?.unapprovedBalance || 0) || topupAmount(pendingTopups);
  const expired = Number(expiryData?.alreadyExpired || 0);
  const soon = Number(expiryData?.expiring0_30 || 0);
  const companyTotal = people.data?.companyDocumentsTotal ?? 0;
  const companyExpired = Number(companyExpiry.data?.alreadyExpired || 0);
  const companySoon = Number(companyExpiry.data?.expiring0_30 || 0);
  const docsTotal = features.EMPLOYEES && !people.error && !people.loading ? (people.data?.documentsTotal ?? null) : null;

  const nowMonth = new Date().getFullYear() * 12 + new Date().getMonth();
  const cardMonth = (c) => {
    const m = /^(\d{1,2})\s*\/\s*(\d{2})$/.exec(c.expiry || '');
    return m ? (2000 + Number(m[2])) * 12 + Number(m[1]) - 1 : null;
  };
  const cardList = (cards.data || []).filter((c) => c.active !== false);
  const expiredCards = cardList.filter((c) => cardMonth(c) !== null && cardMonth(c) < nowMonth);
  const expiringCards = cardList.filter((c) => cardMonth(c) === nowMonth);

  const payTarget = features.PAYMENTS && canAct ? '/portal/payments' : '/portal/invoices';
  const payAction = features.PAYMENTS && canAct ? 'Pay' : 'View';

  // ---- Needs your attention: only the lines that apply ----
  const attention = [
    awaitingQuotes.length > 0 && {
      key: 'quotes', tone: 'amber',
      title: `${plural(awaitingQuotes.length, 'quotation')} waiting for your answer`,
      action: { label: 'Review' },
      onClick: () => navigate(awaitingQuotes.length === 1 ? `/portal/quotations/${awaitingQuotes[0].id}` : '/portal/quotations'),
    },
    overdue.length > 0 && {
      key: 'overdue', tone: 'red',
      title: `${plural(overdue.length, 'invoice')} overdue · ${formatCurrency(overdueTotal)}`,
      action: { label: payAction }, onClick: () => navigate(payTarget),
    },
    toPayList.length > 0 && {
      key: 'pay', tone: 'amber',
      title: `${plural(toPayList.length, 'invoice')} to pay · ${formatCurrency(toPayTotal)}`,
      action: { label: payAction }, onClick: () => navigate(payTarget),
    },
    features.DOC_EXPIRY && (expired > 0 || soon > 0) && {
      key: 'expiry', tone: expired > 0 ? 'red' : 'amber',
      title: [expired > 0 && `${plural(expired, 'document')} expired`, soon > 0 && `${soon} expire within 30 days`].filter(Boolean).join(' · '),
      action: { label: 'View' }, onClick: () => navigate(`/portal/document-expiry?range=${expired > 0 ? 'EXPIRED' : 'R0_30'}`),
    },
    features.PAYMENTS && rejectedTopups.length > 0 && {
      key: 'topup-rejected', tone: 'red',
      title: `${plural(rejectedTopups.length, 'top-up')} rejected · ${formatCurrency(topupAmount(rejectedTopups))}`,
      detail: rejectedTopups[0].rejectionReason ? `Reason: ${rejectedTopups[0].rejectionReason}` : undefined,
      action: { label: 'View' }, onClick: () => navigate('/portal/wallet'),
    },
    features.PAYMENTS && pendingTopups.length > 0 && {
      key: 'topup', tone: 'blue',
      title: `${plural(pendingTopups.length, 'top-up')} waiting for approval · ${formatCurrency(topupAmount(pendingTopups))}`,
      action: { label: 'View' }, onClick: () => navigate('/portal/wallet'),
    },
    onHold.length > 0 && {
      key: 'hold', tone: 'amber',
      title: `${plural(onHold.length, 'work order')} on hold`,
      action: { label: 'View' }, onClick: () => navigate('/portal/work-orders'),
    },
    features.SAVED_CARDS && (expiredCards.length > 0 || expiringCards.length > 0) && {
      key: 'cards', tone: expiredCards.length > 0 ? 'red' : 'amber',
      title: [expiredCards.length > 0 && `${plural(expiredCards.length, 'saved card')} expired`,
        expiringCards.length > 0 && `${plural(expiringCards.length, 'card')} expire${expiringCards.length === 1 ? 's' : ''} this month`].filter(Boolean).join(' · '),
      action: { label: 'View' }, onClick: () => navigate('/portal/cards'),
    },
    pendingReview.length > 0 && {
      key: 'review', tone: 'teal',
      title: `${plural(pendingReview.length, 'work order')} being priced by the typing center`,
      action: { label: 'View' }, onClick: () => navigate('/portal/work-orders'),
    },
  ].filter(Boolean);
  const attentionLoading = [quotations, workOrders, outstanding, topups, expiry, cards].some((s) => s.loading);

  // ---- Recent activity: one timeline instead of three tables ----
  const activity = [
    ...quoteList.map((q) => ({
      id: `q-${q.id}`, date: getField(q, 'quotationDate', 'quotation_date', 'createdAt', 'created_at'),
      text: `Quotation ${getField(q, 'quotationNumber', 'quotation_number', 'draftNumber', 'draft_number') || ''} ${q.status === 'SENT' ? 'sent to you' : (q.status || '').toLowerCase()}`,
      amount: getField(q, 'grandTotal', 'grand_total'), to: `/portal/quotations/${q.id}`,
    })),
    ...woList.map((w) => ({
      id: `w-${w.id}`, date: getField(w, 'orderDate', 'order_date', 'createdAt', 'created_at'),
      text: `Work order ${getField(w, 'workOrderNumber', 'work_order_number') || ''} · ${(w.status || '').replace(/_/g, ' ').toLowerCase()}`,
      amount: getField(w, 'grandTotal', 'grand_total'), to: `/portal/work-orders/${w.id}`,
    })),
    ...invoiceList.map((inv) => ({
      id: `i-${inv.id}`, date: getField(inv, 'invoiceDate', 'invoice_date', 'createdAt', 'created_at'),
      text: `Invoice ${getField(inv, 'invoiceNumber', 'invoice_number') || ''} issued · ${(getField(inv, 'customerPaymentStatus', 'customer_payment_status') || 'unpaid').toLowerCase()}`,
      amount: getField(inv, 'grandTotal', 'grand_total'), to: `/portal/invoices/${inv.id}`,
    })),
  ].filter((a) => a.date).sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 6);
  const activityLoading = quotations.loading || workOrders.loading || invoices.loading;

  const chart = (months.data || []).map((m, i, all) => ({
    month: m.month, amount: m.amount, current: i === all.length - 1,
    label: MONTH_NAMES[Number(m.month.slice(5, 7)) - 1],
  }));
  const invoicedTotal = chart.reduce((s, m) => s + m.amount, 0);

  const cardDefs = [
    {
      key: 'pay', hero: true, label: 'To pay', Icon: PayIcon, tone: 'teal',
      value: show(outstanding, formatCurrency(outstandingTotal), 120),
      lines: [
        show(outstanding, outstandingList.length ? plural(outstandingList.length, 'unpaid invoice') : 'Nothing due — all paid', 110),
        show(invoices, `${invoiceList.length} invoices in total`, 110),
      ],
      action: payAction === 'Pay' && outstandingTotal > 0 ? 'Pay' : 'View',
      onAction: () => navigate(payAction === 'Pay' && outstandingTotal > 0 ? '/portal/payments' : '/portal/invoices'),
      onClick: () => navigate('/portal/invoices'),
    },
    features.PAYMENTS && {
      key: 'wallet', label: 'Wallet', Icon: WalletIcon, tone: 'blue',
      value: show(wallet, formatCurrency(walletBalance), 120),
      lines: [
        show(topups, waitingApproval ? `${formatCurrency(waitingApproval)} waiting approval` : 'No top-up waiting approval', 140),
        show(outstanding, outstandingTotal > 0
          ? (walletBalance >= outstandingTotal
            ? `Covers all invoices · ${formatCurrency(walletBalance - outstandingTotal)} left`
            : `${formatCurrency(outstandingTotal - walletBalance)} short to pay all`)
          : 'Available for new work', 140),
      ],
      action: canAct ? 'Top up' : 'View',
      onAction: () => navigate(canAct ? '/portal/wallet/topup' : '/portal/wallet'),
      onClick: () => navigate('/portal/wallet'),
    },
    {
      key: 'work', label: 'Work orders', Icon: WorkIcon, tone: 'teal',
      value: show(workOrders, `${activeWos.length} active`, 100),
      lines: [
        show(workOrders, pendingReview.length ? `${pendingReview.length} being priced` : `${completedWos.length} completed`, 100),
        features.TASKS && show(tasks, plural(tasks.data || 0, 'open task'), 100),
      ],
      action: canAct && features.CREATE_WORK_ORDER ? '+ New' : 'View',
      onAction: () => navigate(canAct && features.CREATE_WORK_ORDER ? '/portal/work-orders/create' : '/portal/work-orders'),
      onClick: () => navigate('/portal/work-orders'),
    },
    features.EMPLOYEES && {
      key: 'people', label: 'Employees', Icon: PeopleIcon, tone: 'blue',
      value: show(employees, employees.data, 60),
      lines: [
        show(people, `${people.data?.employeesActive ?? '—'} with a valid visa`, 120),
        !features.DOC_EXPIRY && show(people, plural(companyTotal, 'company document'), 120),
      ],
      action: canAct ? '+ Add' : 'View',
      onAction: () => navigate('/portal/employees'),
      onClick: () => navigate('/portal/employees'),
    },
    features.DOC_EXPIRY && {
      key: 'docs', label: 'Documents', Icon: DocsIcon, tone: 'amber',
      value: expiry.loading || people.loading ? <Skeleton width={60} /> : (features.EMPLOYEES ? (docsTotal ?? '—') : expired + soon),
      lines: [
        show(expiry, (
          <>
            {features.EMPLOYEES && (
              <>
                <Box component="span" sx={{ color: 'success.main' }}>{docsTotal == null ? '—' : Math.max(docsTotal - expired, 0)} valid</Box>
                {' · '}
              </>
            )}
            <Box component="span" sx={{ color: expired ? 'error.main' : undefined }}>{expired} expired</Box>
          </>
        ), 140),
        show(expiry, soon ? `${soon} expire within 30 days` : 'Nothing expires in 30 days', 140),
        features.EMPLOYEES && show(companyExpiry, `Company: ${plural(companyTotal, 'document')} · ${companyExpired} expired · ${companySoon} within 30 days`, 200),
      ],
      action: canAct && (expired || soon) ? 'Renew' : 'View',
      onAction: () => navigate(`/portal/document-expiry?range=${expired ? 'EXPIRED' : 'R0_30'}`),
      onClick: () => navigate('/portal/document-expiry'),
    },
  ].filter(Boolean);

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      <Box sx={{ ...gridSx(`repeat(${Math.min(cardDefs.length, 3)}, minmax(0, 1fr))`), mb: 2,
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: `repeat(${Math.min(cardDefs.length, 3)}, minmax(0, 1fr))`, xl: `repeat(${cardDefs.length}, minmax(0, 1fr))` } }}>
        {cardDefs.map((c) => (
          <HeadlineCard
            key={c.key} hero={c.hero} label={c.label} value={c.value} onClick={c.onClick}
            icon={(
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <IconTile Icon={c.Icon} tone={c.tone} hero={c.hero} />
                <CardAction label={c.action} onClick={c.onAction} hero={c.hero} />
              </Box>
            )}
          >
            <Box sx={{ mt: 0.5 }}>
              {c.lines.filter(Boolean).map((l, i) => <Line key={i} hero={c.hero}>{l}</Line>)}
            </Box>
          </HeadlineCard>
        ))}
      </Box>

      <Box sx={{ ...gridSx('1fr 1fr'), mb: 2 }}>
        <DashCard title="Needs your attention" count={attention.length > 0 ? attention.length : undefined}>
          {attention.length === 0 && attentionLoading ? (
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              <Skeleton variant="rounded" height={34} />
              <Skeleton variant="rounded" height={34} />
            </Box>
          ) : attention.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, py: 3, color: 'success.main' }}>
              <AllGoodIcon />
              <Typography sx={{ fontSize: '0.8125rem' }}>All caught up — nothing waiting for you.</Typography>
            </Box>
          ) : (
            <AttentionList items={attention} onAction={(it) => it.onClick()} />
          )}
        </DashCard>

        <DashCard
          title="Invoiced · last 6 months"
          error={months.error ? 'Could not load the invoiced amounts.' : undefined}
        >
          {months.loading ? <Skeleton variant="rounded" height={190} /> : (
            <Box sx={{ height: 210 }}>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: -1, mb: 0.5 }}>{`Total ${formatCurrency(invoicedTotal)}`}</Typography>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chart} margin={{ top: 4, right: 4, left: -12, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={theme.palette.divider} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: theme.palette.text.secondary }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: theme.palette.text.secondary }} tickLine={false} axisLine={false} tickFormatter={formatCompact} />
                  <ChartTooltip formatter={(v) => formatCurrency(v)} />
                  <Bar dataKey="amount" name="Invoiced" radius={[6, 6, 0, 0]}>
                    {chart.map((d) => <Cell key={d.month} fill={d.current ? '#2a9d8f' : '#9fd3cc'} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}
        </DashCard>
      </Box>

      <DashCard title="Recent activity" empty={!activityLoading && activity.length === 0} emptyText="Nothing yet.">
        {activityLoading && activity.length === 0 ? <Skeleton variant="rounded" height={90} /> : activity.map((a, i) => (
          <Box key={a.id} onClick={() => navigate(a.to)}
            sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.9, cursor: 'pointer', borderTop: i ? '1px solid' : 0, borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' } }}>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', width: 80, flexShrink: 0 }}>{formatDate(a.date, '—')}</Typography>
            <Typography sx={{ fontSize: '0.8125rem', flex: 1, minWidth: 0, '&::first-letter': { textTransform: 'uppercase' } }} noWrap>{a.text}</Typography>
            {a.amount != null && <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{formatCurrency(a.amount)}</Typography>}
          </Box>
        ))}
      </DashCard>
    </Box>
  );
};

export default PortalDashboard;
